import type { ColliderHandle, RoomCapabilities, RoomEnvironment, RoomModule, RoomModuleDefinition } from '@/game/roomModule';
import type { HedgerowDebug } from '@/game/roomDiagnostics';
import type { ZoneLightingOptions } from '@/render/lighting';
import { getZone } from '@/render/palette';
import {
  BOARDS_AT, DAWN_ZENITH, HEDGEROW_BLOOM_ZONE_ID, HEDGEROW_NIGHT_SPAWN, HEDGEROW_RAIN, HEDGEROW_SPAWN, HEDGEROW_ZONE_ID,
  NIGHT_ZENITH, createHedgerow, hedgerowAmbience, hedgerowLighting, hedgerowNightLighting, hedgerowNightZone, hedgerowSolids,
} from '@/world/hedgerow';
import type { HallSolid } from '@/world/solids';

/**
 * Beat 7's field and beat 10's — the same ground at two hours, two room ids
 * (D-222) — as room modules. Until session 80 this was the last exterior
 * branch in `main.ts` that knew a beat.
 *
 * **The room is static.** Everything that moves in either beat is its part's
 * (`OUT_PART_MODULE`, `TWELVE_OUT_PART_MODULE`, D-223/D-224); what is left
 * here is ground, dressing, light, sky, rain and the one seam a part reaches
 * through — the bay in the field fence.
 *
 * **Both rooms build the bay whole, with its collider.** Beat 7 breaks it by
 * the roll; beat 10 opens on the hole she already made (D-173) and breaks it
 * at its own `create`. Keyed on the part and not the room, so
 * `?room=hedgerow-night` with no beat — the census and shot-run control —
 * keeps the whole bay it has always had (D-223, "the night control's bay").
 * `beat-10-gap.spec.ts` is the delta that goes red if either half is lost.
 */

/**
 * The bay's collider, which stops her until beat 7's roll breaks it. Invisible:
 * the bay's mesh is the dressing's, and the mesh swap and this collider's
 * removal are one event, owned by the part through `HedgerowBoardsSeam`.
 */
const BAY_SOLID: HallSolid = {
  name: 'hedgerow-boards',
  half: { x: 1, y: 1.075, z: 0.12 },
  pos: { x: BOARDS_AT.x, y: 1.075, z: BOARDS_AT.z },
  colour: 0,
  kind: 'organic',
  visible: false,
};

function authored(lighting: ZoneLightingOptions): { key: number; exposureReference: number } {
  const { keyIntensity, hemiIntensity, ambientIntensity } = lighting;
  if (keyIntensity === undefined || hemiIntensity === undefined || ambientIntensity === undefined) {
    throw new Error('hedgerow: lighting is missing an authored intensity');
  }
  return { key: keyIntensity, exposureReference: lighting.exposureReference ?? keyIntensity + hemiIntensity + ambientIntensity };
}

/**
 * **Beat 7 boots in the `yard` zone, not in `outside`.** She is at the fence in
 * the last of a storm, on the farm's side of it; the palette she starts in is
 * the one she has been in for a beat. `outside` arrives through the bloom and
 * only through the bloom (D-117) — which is why the sky is baked from
 * `outside`, not from the room's zone, and revealed by `backgroundIntensity`
 * (D-106: `scene.background` holds exactly one texture). Before the bloom it is
 * a dark dawn sky behind haze, in a beat whose first four stages point the
 * camera at the ground. See `src/render/bloom.ts`.
 */
function dayEnvironment(): RoomEnvironment {
  const zone = getZone(HEDGEROW_ZONE_ID);
  if (zone === undefined) throw new Error(`unknown zone "${HEDGEROW_ZONE_ID}"`);
  const bloomZone = getZone(HEDGEROW_BLOOM_ZONE_ID);
  if (bloomZone === undefined) throw new Error(`unknown zone "${HEDGEROW_BLOOM_ZONE_ID}"`);
  return {
    zone,
    lighting: hedgerowLighting(),
    background: { kind: 'sky', seed: 7, zenith: DAWN_ZENITH, zone: bloomZone },
    spawn: HEDGEROW_SPAWN,
    ambience: hedgerowAmbience(),
    rain: HEDGEROW_RAIN,
  };
}

/**
 * Beat 10 is the storm gone: no rain, no bloom zone. The room says so by
 * declaring none, rather than inheriting beat 7's weather because both beats
 * share the same authored geometry.
 */
function nightEnvironment(): RoomEnvironment {
  return {
    zone: hedgerowNightZone(),
    lighting: hedgerowNightLighting(),
    background: { kind: 'sky', seed: 7, zenith: NIGHT_ZENITH },
    spawn: HEDGEROW_NIGHT_SPAWN,
    ambience: hedgerowAmbience(),
  };
}

function createHedgerowRoom(night: boolean, dressing: boolean, capabilities: RoomCapabilities): RoomModule {
  // `?dress=0` builds the room without its dressing, and the ONLY caller is
  // `render-budget.spec.ts`'s empty-versus-dressed delta (D-100). See
  // `HedgerowOptions.dressing` for why the control has to be this room rather
  // than this room from a different camera.
  const room = createHedgerow({ dressing, night, boards: 'whole' });
  // The room bakes its own sky; the environment declares the same bake and
  // the host owns that one (the hill does the same).
  room.sky.dispose();
  capabilities.scene.add(room.group);
  // **`solid.pitch` is carried by the host's collider, and it is the whole
  // reason this room's floor is not a lie.** Three of its solids are pitched —
  // the fall and the ditch's two banks — and `solidMeshes()` bakes the same
  // angle into the geometry (D-114, D-056/D-059).
  const colliders: ColliderHandle[] = hedgerowSolids().map((solid) => capabilities.colliders.add(solid));
  let bay: ColliderHandle | null = capabilities.colliders.add(BAY_SOLID);
  const lighting = authored(night ? hedgerowNightLighting() : hedgerowLighting());
  const zoneId = night ? hedgerowNightZone().id : HEDGEROW_ZONE_ID;
  const debug: HedgerowDebug = { night, zoneId, keyIntensity: lighting.key, exposureReference: lighting.exposureReference };
  let disposed = false;
  return {
    interactables: [],
    fixedStep(): void { /* static: the beats' parts own everything that moves */ },
    renderStep(): void { /* static */ },
    framing: () => null,
    diagnostics: () => ({ kind: 'hedgerow', hedgerow: debug }),
    hedgerowBoards: {
      boards: room.boards,
      removeBoardsCollider(): void {
        if (bay === null) return;
        capabilities.colliders.remove(bay);
        bay = null;
      },
    },
    dispose(): void {
      if (disposed) return;
      disposed = true;
      for (const handle of colliders) capabilities.colliders.remove(handle);
      if (bay !== null) capabilities.colliders.remove(bay);
      bay = null;
      capabilities.scene.remove(room.group);
    },
  };
}

export const HEDGEROW_ROOM_MODULE: RoomModuleDefinition = {
  room: 'hedgerow',
  environment: dayEnvironment,
  create: (options, capabilities) => createHedgerowRoom(false, options.dressing, capabilities),
};

export const HEDGEROW_NIGHT_ROOM_MODULE: RoomModuleDefinition = {
  room: 'hedgerow-night',
  // Beat 7's origin and metres (D-222): her notes go on the day field's sheet.
  ledgerSheet: 'hedgerow',
  environment: nightEnvironment,
  create: (options, capabilities) => createHedgerowRoom(true, options.dressing, capabilities),
};
